import React from 'react';
import styled from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead
import { useBooking } from '../../../../contexts/BookingContext';

const TicketContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 1rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const TicketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TicketItem = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem;
  border: 2px solid ${props => props.isSelected ? props.theme.colors.accent : 'transparent'};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const TicketInfo = styled.div`
  flex: 1;
`;

const TicketName = styled.h3`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const TicketDescription = styled.p`
  color: ${props => props.theme.colors.secondary};
  line-height: 1.4;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const TicketPrice = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
`;

const TicketControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border-radius: 8px;
  padding: 0.5rem;
  border: 1px solid ${props => props.theme.colors.greyBackground};
`;

const QuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: ${props => props.theme.colors.accent};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }
  
  &:disabled {
    background: ${props => props.theme.colors.greyBackground};
    color: ${props => props.theme.colors.secondary};
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityDisplay = styled.span`
  min-width: 2rem;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const TicketDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  
  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
  border: 1px solid ${props => props.theme.colors.accent};
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const SummaryPanel = styled.div`
  position: sticky;
  top: 90px;
  height: fit-content;
  background: white;
  border-radius: 10px;
  padding: 1rem;
  border: 2px solid ${props => props.theme.colors.accent};
`;

const TotalLabel = styled.div`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const TotalAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
`;

const Subrow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.25rem 0;
  font-size: 0.95rem;
`;

const ContinueButton = styled.button`
  width: 100%;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  &:disabled {
    background: ${props => props.theme.colors.greyBackground};
    color: ${props => props.theme.colors.secondary};
    cursor: not-allowed;
    transform: none;
  }
`;

const TicketSelection = ({ tickets, selectedTickets, onTicketUpdate, onContinue }) => {
  const { getSubtotal, getGroupDiscountAmount, calculateTotal } = useBooking();
  // Map selected quantities by id for O(1) lookup
  const qtyById = React.useMemo(() => {
    const map = new Map();
    (selectedTickets || []).forEach(t => {
      if (t && t.webSaleItemId != null) map.set(String(t.webSaleItemId), Number(t.quantity || 0));
    });
    return map;
  }, [selectedTickets]);
  const handleQuantityChange = (ticketId, change) => {
    const ticket = selectedTickets.find(t => t.webSaleItemId === ticketId);
    const currentQuantity = ticket ? ticket.quantity : 0;
    const newQuantity = Math.max(0, Math.min(currentQuantity + change, 10)); // Max 10 tickets per type
    
    onTicketUpdate(ticketId, newQuantity);
  };

  const getSelectedQuantity = (ticketId) => qtyById.get(String(ticketId)) || 0;

  const getTotalTickets = () => {
    return selectedTickets.reduce((total, ticket) => total + ticket.quantity, 0);
  };

  return (
    <TicketContainer>
      <Layout>
        <TicketList>
        {tickets.map(ticket => {
          const selectedQuantity = getSelectedQuantity(ticket.webSaleItemId);
          const isSelected = selectedQuantity > 0;
          
          return (
            <TicketItem key={ticket.webSaleItemId} isSelected={isSelected}>
              <TicketHeader>
                <TicketInfo>
                  <TicketName>{ticket.name}</TicketName>
                  <TicketDescription>{ticket.description}</TicketDescription>
                  <TicketPrice>£{ticket.price.toFixed(2)} each</TicketPrice>
                </TicketInfo>
                
                <TicketControls>
                  <QuantitySelector>
                    <QuantityButton
                      onClick={() => handleQuantityChange(ticket.webSaleItemId, -1)}
                      disabled={selectedQuantity === 0}
                    >
                      −
                    </QuantityButton>
                    <QuantityDisplay>{selectedQuantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => handleQuantityChange(ticket.webSaleItemId, 1)}
                      disabled={selectedQuantity >= 10}
                    >
                      +
                    </QuantityButton>
                  </QuantitySelector>
                </TicketControls>
              </TicketHeader>
              
              {isSelected && (
                <TicketDetails>
                  <DetailItem>
                    <span>ℹ️ Quantity: {selectedQuantity}</span>
                  </DetailItem>
                  <DetailItem>
                    <span>ℹ️ Subtotal: £{(ticket.price * selectedQuantity).toFixed(2)}</span>
                  </DetailItem>
                  {ticket.maxPurchaseQty && ticket.maxPurchaseQty !== 999 && (
                    <DetailItem>
                      <span>ℹ️ Max per booking: {ticket.maxPurchaseQty}</span>
                    </DetailItem>
                  )}
                </TicketDetails>
              )}
            </TicketItem>
          );
        })}
        </TicketList>

        <SummaryPanel>
          {getTotalTickets() > 0 ? (
            <>
              <TotalLabel>Total ({getTotalTickets()} ticket{getTotalTickets() !== 1 ? 's' : ''})</TotalLabel>
              <Subrow>
                <span>Subtotal</span>
                <span>£{getSubtotal().toFixed(2)}</span>
              </Subrow>
              {getGroupDiscountAmount() > 0 && (
                <Subrow>
                  <span>Family Saver</span>
                  <span>-£{getGroupDiscountAmount().toFixed(2)}</span>
                </Subrow>
              )}
              <Subrow>
                <strong>Today</strong>
                <strong>£{calculateTotal().toFixed(2)}</strong>
              </Subrow>
              {onContinue && (
                <ContinueButton onClick={onContinue}>
                  Continue →
                </ContinueButton>
              )}
            </>
          ) : (
            <InfoText>
              ℹ️ Select the tickets you'd like to purchase. You can adjust quantities using the + and - buttons.
            </InfoText>
          )}
        </SummaryPanel>
      </Layout>
    </TicketContainer>
  );
};

export default TicketSelection;